import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';

export default function AdminDashboard() {
  const { token, user } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch('http://localhost:3001/api/admin/stats', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        setError('Failed to load admin stats');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!user?.role === 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600 text-xl">Access Denied: Admin access required</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <div className="text-sm text-gray-600">Logged in as: {user?.email}</div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-gray-600 text-sm font-semibold uppercase tracking-wide">
                Total Products
              </div>
              <div className="mt-2 flex items-baseline">
                <div className="text-5xl font-semibold text-blue-600">
                  {stats.totalProducts || 0}
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-gray-600 text-sm font-semibold uppercase tracking-wide">
                Total Orders
              </div>
              <div className="mt-2 flex items-baseline">
                <div className="text-5xl font-semibold text-green-600">
                  {stats.totalOrders || 0}
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-gray-600 text-sm font-semibold uppercase tracking-wide">
                Revenue (MTD)
              </div>
              <div className="mt-2 flex items-baseline">
                <div className="text-5xl font-semibold text-purple-600">
                  ${(stats.revenueThisMonth || 0).toFixed(2)}
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-gray-600 text-sm font-semibold uppercase tracking-wide">
                Pending Jobs
              </div>
              <div className="mt-2 flex items-baseline">
                <div className="text-5xl font-semibold text-orange-600">
                  {stats.pendingJobs || 0}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <a
            href="/admin/products"
            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition cursor-pointer"
          >
            <div className="text-2xl mb-2">📦</div>
            <h3 className="text-lg font-semibold text-gray-900">Product Management</h3>
            <p className="text-gray-600 text-sm mt-2">Add, edit, or delete products</p>
          </a>

          <a
            href="/admin/orders"
            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition cursor-pointer"
          >
            <div className="text-2xl mb-2">🛒</div>
            <h3 className="text-lg font-semibold text-gray-900">Order Management</h3>
            <p className="text-gray-600 text-sm mt-2">View and manage customer orders</p>
          </a>

          <a
            href="/admin/provisioning"
            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition cursor-pointer"
          >
            <div className="text-2xl mb-2">⚙️</div>
            <h3 className="text-lg font-semibold text-gray-900">Provisioning Jobs</h3>
            <p className="text-gray-600 text-sm mt-2">Monitor and retry provisioning tasks</p>
          </a>

          <a
            href="/admin/customers"
            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition cursor-pointer"
          >
            <div className="text-2xl mb-2">👥</div>
            <h3 className="text-lg font-semibold text-gray-900">Customers</h3>
            <p className="text-gray-600 text-sm mt-2">View all customer accounts</p>
          </a>

          <a
            href="/admin/domain-settings"
            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition cursor-pointer"
          >
            <div className="text-2xl mb-2">🌐</div>
            <h3 className="text-lg font-semibold text-gray-900">Domain Pricing</h3>
            <p className="text-gray-600 text-sm mt-2">Configure pricing markup</p>
          </a>
        </div>
      </div>
    </div>
  );
}
